import React, { useMemo } from 'react';
import { Card, Row, Col, Statistic, Descriptions, Space, Typography, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CalculatorData } from './Calculator';
import { LAUNCH_BUDGET_DEFAULT } from '../../shared/lib/budget';
import { MAX_PURCHASE_PRICE_TARGET_MARGIN, computeUnitEconomics } from '../../shared/lib/unitEconomics';
import { useAppColors } from '../../shared/theme';

interface CalculatorSummaryProps {
  data?: CalculatorData;
  launchBudget?: number;
  couponPromoEnabled?: boolean;
}

const CalculatorSummary: React.FC<CalculatorSummaryProps> = ({ data, launchBudget = LAUNCH_BUDGET_DEFAULT, couponPromoEnabled = false }) => {
  const c = useAppColors();
  const { t } = useTranslation();

  const defaults: CalculatorData = {
    purchasePrice: 0, shippingCostPerKg: 0, unitWeightKg: 0, shippingPrice: 0,
    storageFee: 0.1,
    sellingPrice: 0, referralFee: 0, fbaFee: 0, otherFees: 0,
    ppcCost: 0, conversionRate: 1, ppcSalesPercentage: 70,
    returnRate: 0, resellableReturns: 75,
    monthlySales: 0, batchQuantity: 400,
    couponPromoEnabled: false,
  };

  const d = data ?? defaults;

  // Расчёт живёт в shared/lib/unitEconomics.ts: формулы, по которым принимают
  // решение о закупке, обязаны быть проверяемы отдельно от рендера.
  const {
    cogs,
    referralFeeAmount,
    returnLossPerUnit,
    grossProfitPerUnit,
    marginPerUnit,
    grossRoi,
    batchQty,
    batchCogs,
    batchPpcCost,
    batchStorageFee,
    couponPromoCost,
    batchProfit,
    batchMargin,
    batchROI,
    batchProfitWithLaunch,
    batchMarginWithLaunch,
    batchROIWithLaunch,
    returnQty,
    batchReturnLoss,
    orderCost,
    ppcOrderProfit,
    advertisingCostPerUnit,
    profitPerUnitWithCosts,
    marginPerUnitWithCosts,
    roiPerUnitWithCosts,
    maxPurchasePrice,
  } = useMemo(
    () => computeUnitEconomics(d, launchBudget, couponPromoEnabled),
    [d, launchBudget, couponPromoEnabled],
  );

  const vs = (color: string, fontSize: number, fontWeight?: number): React.CSSProperties => ({
    color,
    fontSize,
    ...(fontWeight ? { fontWeight } : {}),
  });

  return (
    <Card title={t('idea_calc.calc_summary.title')} variant="outlined" style={{ height: '100%', flex: 1, minWidth: 0 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {/* Per-unit metrics - gross profit (without PPC/storage) */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.calc_summary.section_gross_per_unit')}</Typography.Text>
        <Row gutter={[8, 8]}>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: grossProfitPerUnit >= 0 ? c.successBg : c.errorBg, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_profit_per_unit')}
                value={grossProfitPerUnit}
                precision={2}
                prefix="$"
                valueStyle={vs(grossProfitPerUnit >= 0 ? c.success : c.error, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: marginPerUnit >= 0 ? c.successBg : c.errorBg, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_margin')}
                value={marginPerUnit}
                precision={2}
                suffix="%"
                valueStyle={vs(marginPerUnit >= 0 ? c.success : c.error, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: grossRoi >= 0 ? c.successBg : c.errorBg, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_roi')}
                value={grossRoi}
                precision={2}
                suffix="%"
                valueStyle={vs(grossRoi >= 0 ? c.success : c.error, 18)}
              />
            </Card>
          </Col>
        </Row>

        {/* Per-unit metrics - with ALL costs (PPC, storage, returns) but WITHOUT launch budget */}
        <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
          {t('idea_calc.calc_summary.section_with_costs')}
        </Typography.Text>
        <Row gutter={[8, 8]}>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: profitPerUnitWithCosts >= 0 ? c.infoBg : c.errorBg, border: `1px solid ${c.infoBorder}`, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_profit_per_unit')}
                value={profitPerUnitWithCosts}
                precision={2}
                prefix="$"
                valueStyle={vs(profitPerUnitWithCosts >= 0 ? c.info : c.error, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: marginPerUnitWithCosts >= 0 ? c.infoBg : c.errorBg, border: `1px solid ${c.infoBorder}`, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_margin')}
                value={marginPerUnitWithCosts}
                precision={2}
                suffix="%"
                valueStyle={vs(marginPerUnitWithCosts >= 0 ? c.info : c.error, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: roiPerUnitWithCosts >= 0 ? c.infoBg : c.errorBg, border: `1px solid ${c.infoBorder}`, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_roi')}
                value={roiPerUnitWithCosts}
                precision={2}
                suffix="%"
                valueStyle={vs(roiPerUnitWithCosts >= 0 ? c.info : c.error, 18)}
              />
            </Card>
          </Col>
        </Row>
        {/* Второй ряд той же секции — «три плитки» (фидбек «Пайплайн 3_08», 22.09.2026):
            МЗЦ четвёртой плиткой в ряду выше переносилась и растягивалась на всю
            ширину, а цена ордера и профит с ордера PPC стояли парой внизу. МЗЦ —
            расчётное поле, ввода нет, только показ. Ширина плиток делится поровну
            (`1 1 0`), а не от базиса 160px: в колонке «Сводка» базис переносил третью
            плитку на всю строку — ровно то, на что жаловались. */}
        <Row gutter={[8, 8]}>
          <Col flex="1 1 0" style={{ minWidth: 0, display: 'flex' }}>
            <Card size="small" style={{ flex: 1, background: maxPurchasePrice >= 0 ? c.infoBg : c.errorBg, border: `1px solid ${c.infoBorder}`, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_max_purchase_price')}
                value={maxPurchasePrice}
                precision={2}
                prefix="$"
                valueStyle={vs(maxPurchasePrice >= 0 ? c.info : c.error, 18)}
              />
              <Typography.Text type="secondary" style={{ fontSize: 9 }}>
                {t('idea_calc.calc_summary.stat_max_purchase_price_hint', { margin: MAX_PURCHASE_PRICE_TARGET_MARGIN * 100 })}
              </Typography.Text>
            </Card>
          </Col>
          <Col flex="1 1 0" style={{ minWidth: 0, display: 'flex' }}>
            <Card size="small" style={{ flex: 1, background: c.infoBg, border: `1px solid ${c.infoBorder}`, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_order_cost')}
                value={orderCost}
                precision={2}
                prefix="$"
                valueStyle={vs(c.info, 18)}
              />
              <Typography.Text type="secondary" style={{ fontSize: 9 }}>{t('idea_calc.calc_summary.stat_order_cost_formula')}</Typography.Text>
            </Card>
          </Col>
          <Col flex="1 1 0" style={{ minWidth: 0, display: 'flex' }}>
            <Card size="small" style={{ flex: 1, background: ppcOrderProfit >= 0 ? c.successBg : c.errorBg, border: `1px solid ${ppcOrderProfit >= 0 ? c.successBorder : c.errorBorder}`, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_ppc_order_profit')}
                value={ppcOrderProfit}
                precision={2}
                prefix="$"
                valueStyle={vs(ppcOrderProfit >= 0 ? c.success : c.error, 18)}
              />
              <Typography.Text type="secondary" style={{ fontSize: 9 }}>{t('idea_calc.calc_summary.stat_ppc_order_profit_formula')}</Typography.Text>
            </Card>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0' }} />

        {/* Batch analysis — without launch costs */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {t('idea_calc.calc_summary.section_batch_no_launch', { qty: batchQty })}
        </Typography.Text>
        <Row gutter={[8, 8]}>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: batchProfit >= 0 ? c.successBg : c.errorBg, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_profit')}
                value={batchProfit}
                precision={2}
                prefix="$"
                valueStyle={vs(batchProfit >= 0 ? c.success : c.error, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: batchMargin >= 0 ? c.successBg : c.errorBg, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_margin')}
                value={batchMargin}
                precision={2}
                suffix="%"
                valueStyle={vs(batchMargin >= 0 ? c.success : c.error, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: batchROI >= 0 ? c.successBg : c.errorBg, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_roi')}
                value={batchROI}
                precision={2}
                suffix="%"
                valueStyle={vs(batchROI >= 0 ? c.success : c.error, 18)}
              />
            </Card>
          </Col>
        </Row>

        {/* Batch analysis — with launch costs */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {t('idea_calc.calc_summary.section_batch_with_launch', { qty: batchQty, budget: launchBudget.toLocaleString() })}
        </Typography.Text>
        <Row gutter={[8, 8]} style={{ display: 'flex' }}>
          <Col flex="1 0 160px" style={{ display: 'flex' }}>
            <Card
              size="small"
              style={{
                flex: 1,
                textAlign: 'center',
                background: batchProfitWithLaunch >= 0 ? c.successBg : c.errorBg,
                border: batchProfitWithLaunch >= 0 ? `2px solid ${c.success}` : `2px solid ${c.error}`,
              }}
            >
              <Statistic
                title={t('idea_calc.calc_summary.stat_profit')}
                value={batchProfitWithLaunch}
                precision={2}
                prefix="$"
                valueStyle={vs(batchProfitWithLaunch >= 0 ? c.success : c.error, 18, 600)}
              />
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {batchProfitWithLaunch >= 0
                  ? t('idea_calc.calc_summary.covers_launch')
                  : t('idea_calc.calc_summary.missing_amount', { amount: Math.abs(batchProfitWithLaunch).toLocaleString() })}
              </Typography.Text>
            </Card>
          </Col>
          <Col flex="1 0 160px" style={{ display: 'flex' }}>
            <Card
              size="small"
              style={{
                flex: 1,
                textAlign: 'center',
                background: batchMarginWithLaunch >= 0 ? c.successBg : c.errorBg,
                border: batchProfitWithLaunch >= 0 ? `2px solid ${c.success}` : `2px solid ${c.error}`,
              }}
            >
              <Statistic
                title={t('idea_calc.calc_summary.stat_margin')}
                value={batchMarginWithLaunch}
                precision={2}
                suffix="%"
                valueStyle={vs(batchMarginWithLaunch >= 0 ? c.success : c.error, 18, 600)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px" style={{ display: 'flex' }}>
            <Card
              size="small"
              style={{
                flex: 1,
                textAlign: 'center',
                background: batchROIWithLaunch >= 0 ? c.successBg : c.errorBg,
                border: batchProfitWithLaunch >= 0 ? `2px solid ${c.success}` : `2px solid ${c.error}`,
              }}
            >
              <Statistic
                title={t('idea_calc.calc_summary.stat_roi')}
                value={batchROIWithLaunch}
                precision={2}
                suffix="%"
                valueStyle={vs(batchROIWithLaunch >= 0 ? c.success : c.error, 18, 600)}
              />
            </Card>
          </Col>
        </Row>

        <Descriptions size="small" bordered column={2} style={{ marginTop: 8 }} styles={{ content: { whiteSpace: 'nowrap' } }}>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_batch_cogs')}>${batchCogs.toFixed(0)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_storage_fee')}>${batchStorageFee.toFixed(0)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_batch_ppc')}>${batchPpcCost.toFixed(0)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_launch_budget')}>${launchBudget.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_returns')}>
            <span style={{ color: c.error }}>{t('idea_calc.calc_summary.row_returns_qty_loss', { qty: returnQty, loss: batchReturnLoss.toFixed(0) })}</span>
          </Descriptions.Item>
          {couponPromoEnabled && (
            <Descriptions.Item label={t('idea_calc.calc_summary.row_coupon_promo')}>
              <span style={{ color: c.warning }}>${couponPromoCost.toFixed(0)}</span>
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider style={{ margin: '8px 0' }} />

        {/* Cost breakdown per unit */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.calc_summary.section_cost_breakdown')}</Typography.Text>
        <Descriptions size="small" bordered column={2} styles={{ content: { whiteSpace: 'nowrap' } }}>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_cogs_label')}>${cogs.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_referral')}>${referralFeeAmount.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_fba')}>${d.fbaFee.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_storage')}>${d.storageFee.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_ppc_per_unit')}>${advertisingCostPerUnit.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_returns_per_unit')}>${returnLossPerUnit.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={<strong>{t('idea_calc.calc_summary.row_gross_profit')}</strong>} span={2}>
            <strong>${grossProfitPerUnit.toFixed(2)}</strong>
          </Descriptions.Item>
        </Descriptions>
      </Space>
    </Card>
  );
};

export default React.memo(CalculatorSummary);
